import { buildAbility } from "./buildAbility"
import type { AppAbility, TenantRolesByTenantId } from "./types"


type SessionUserLike = {
  id?: unknown
  signedInTenants?: unknown
  tenantRoles?: unknown
}

type SessionLike = {
  user?: SessionUserLike
}

const normalizeRole = (raw: unknown): string | null => {
  if (typeof raw !== "string") return null
  const normalized = raw.trim()
  return normalized ? normalized : null
}

const normalizeRoles = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw.map(normalizeRole).filter((r): r is string => Boolean(r))
  }
  const role = normalizeRole(raw)
  return role ? [role] : []
}

const normalizeRolesByTenantId = (raw: unknown): TenantRolesByTenantId | null => {
  if (!raw || typeof raw !== "object") return null
  if (raw instanceof Map) {
    return Object.fromEntries(Array.from(raw.entries()).map(([key, value]) => [String(key), normalizeRoles(value)]))
  }
  const obj = raw as Record<string, unknown>
  const out: TenantRolesByTenantId = {}
  for (const [key, value] of Object.entries(obj)) {
    const tenantId = String(key).trim()
    if (!tenantId) continue
    out[tenantId] = normalizeRoles(value)
  }
  return out
}

export const getTenantRolesFromSessionUser = (user: unknown, tenantId: string): string[] => {
  if (!user || typeof user !== "object") return []
  const rolesByTenantId = normalizeRolesByTenantId((user as SessionUserLike).tenantRoles)
  if (!rolesByTenantId) return []
  return rolesByTenantId[tenantId]?.filter(Boolean) ?? []
}

export const buildAbilityFromSession = ({
  tenantId,
  session,
  claims,
}: {
  tenantId: string
  session: SessionLike | null | undefined
  claims?: Record<string, unknown>
}): AppAbility => {
  const user = session?.user
  const userId = typeof user?.id === "string" ? user.id.trim() : ""
  const rolesByTenantId = normalizeRolesByTenantId(user?.tenantRoles)
  const hasExplicitTenantEntry = Boolean(
    rolesByTenantId && Object.prototype.hasOwnProperty.call(rolesByTenantId, tenantId),
  )

  const signedInTenantsRaw = user?.signedInTenants
  const signedInTenants = Array.isArray(signedInTenantsRaw) ? signedInTenantsRaw.map(String) : []

  const roles = hasExplicitTenantEntry
    ? rolesByTenantId![tenantId] ?? []
    : userId && signedInTenants.includes(tenantId)
      ? ["owner"]
      : getTenantRolesFromSessionUser(user, tenantId)

  return buildAbility({
    tenantId,
    userId: userId || null,
    roles,
    claims,
  })
}
